import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Undo2 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

interface SendBackToFieldDialogProps {
    open: boolean;
    onClose: () => void;
    jobId: string;
    jobLabel?: string | null;
    techName?: string | null;
}

/**
 * "Send back to field", with the why riding along (#815).
 *
 * The old control was a bare status flip to in_progress — no reason, no
 * recipient, nothing on the tech's phone (the close-out audit's C2: "the
 * office can push work back, but it cannot tell anyone why"). This dialog
 * requires the why-note and the server delivers it: the status flip and an
 * URGENT task assigned to the tech (notification + push + SMS) are one act.
 */
export function SendBackToFieldDialog({ open, onClose, jobId, jobLabel, techName }: SendBackToFieldDialogProps) {
    const qc = useQueryClient();
    const [reason, setReason] = useState("");

    const sendBack = useMutation({
        mutationFn: () =>
            api.post<{ ok: true; techName: string | null }>(`/api/jobs/${jobId}/send-back-to-field`, {
                reason: reason.trim(),
            }),
        onSuccess: (data) => {
            toast.success(
                data.techName
                    ? `Sent back to ${data.techName} — they get the reason as an urgent task (push + text).`
                    : "Sent back to the field — the tech gets the reason as an urgent task.",
            );
            qc.invalidateQueries({ queryKey: ["job", jobId] });
            qc.invalidateQueries({ queryKey: ["jobs"] });
            setReason("");
            onClose();
        },
        onError: (err: unknown) => {
            const body = (err as { body?: { error?: string; message?: string } }).body;
            toast.error(
                body?.error === "NO_ASSIGNED_TECH"
                    ? "This job has no assigned technician — assign one first, then send it back."
                    : `Couldn't send back: ${body?.message ?? (err instanceof Error ? err.message : "unknown error")}`,
            );
        },
    });

    const canSubmit = reason.trim().length >= 3 && !sendBack.isPending;

    return (
        <Dialog open={open} onClose={onClose} title="Send back to field">
            <div className="space-y-4">
                <p className="text-sm text-foreground/80">
                    {jobLabel ? <span className="font-mono font-medium">{jobLabel}</span> : "This job"} goes back to{" "}
                    {techName ? <span className="font-medium">{techName}</span> : "the assigned technician"}, and your
                    note below reaches them as an <span className="font-medium">urgent task</span> — notification, push,
                    and text. They&rsquo;ll see exactly what you write.
                </p>
                <div className="space-y-1.5">
                    <Label htmlFor="send-back-reason">What needs fixing? *</Label>
                    <Textarea
                        id="send-back-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder={'e.g. "The 1″ RPZ is missing its after photo — please add it."'}
                        rows={3}
                        autoFocus
                    />
                </div>
                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" onClick={onClose} disabled={sendBack.isPending}>
                        Never mind
                    </Button>
                    <Button disabled={!canSubmit} onClick={() => sendBack.mutate()}>
                        {sendBack.isPending ? (
                            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                            <Undo2 className="h-4 w-4 mr-1" />
                        )}
                        Send back with note
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
